import { gql } from "@apollo/client";
import {
  CategoryNoNestingFragment,
  DepartmentNoNestingFragment,
  MarkupNoNestingFragment,
  PaginatedListMetaNoNestingFragment,
  ProductKitItemNoNestingFragment,
  ProductKitNoNestingFragment,
  ProductLocationInventoryPricingNoNestingFragment,
  ProductNoNestingFragment,
  ProductOptionGroupTemplateNoNestingFragment,
} from "@towersystems/roam-common/lib/generated-graphql-fragment";

const ProductFragment = `
${ProductNoNestingFragment}
${ProductLocationInventoryPricingNoNestingFragment}
${ProductKitNoNestingFragment}
${ProductKitItemNoNestingFragment}
${CategoryNoNestingFragment}
${DepartmentNoNestingFragment}
${MarkupNoNestingFragment}
fragment CustomProductFragment on Product {
  ...ProductNoNesting
      image {
        path
      }
      locationInventoryPricing {
        ...ProductLocationInventoryPricingNoNesting
        location {
          id
          name
        }
      }
      options {
        id
        name
        position
      }
      category {
        ...CategoryNoNesting
        markup {
          ...MarkupNoNesting
        }
      }
      department {
        ...DepartmentNoNesting
        markup {
          ...MarkupNoNesting
        }
      }
      selectedOptions {
        name
        value
      }
      productKit {
        ...ProductKitNoNesting
        items {
          ...ProductKitItemNoNesting
          product {
            ...ProductNoNesting
            image {
              path
            }
          }
        }
      }
      variantParent {
        id
        name
        stockNumber
        image {
          path
        }
        options {
          id
          name
          position
        }
      }
      variants {
        id
        displayName
        stockCode
        stockNumber
        image {
          path
        }
      }
}
`;

export const MUTATION_CREATE_PRODUCT = gql`
  ${ProductFragment}
  mutation CreateProduct($input: CreateProductInput!) {
    createProduct(input: $input) {
      ...CustomProductFragment
    }
  }
`;

export const MUTATION_UPDATE_PRODUCT = gql`
  ${ProductFragment}
  mutation UpdateProduct($input: UpdateProductInput!) {
    updateProduct(input: $input) {
      ...CustomProductFragment
    }
  }
`;

export const QUERY_PRODUCT_LIST = gql`
  ${PaginatedListMetaNoNestingFragment}
  ${ProductNoNestingFragment}
  ${ProductKitNoNestingFragment}
  query products($options: ProductListOptions) {
    products(options: $options) {
      items {
        ...ProductNoNesting
        productKit {
          ...ProductKitNoNesting
        }
        image {
          path
        }
        options {
          id
          name
          position
        }
      }
      meta {
        ...PaginatedListMetaNoNesting
      }
    }
  }
`;

export const MUTATION_GENERATE_BARCODE = gql`
  mutation GenerateBarcode {
    generateProductBarcode
  }
`;

export const QUERY_PRODUCT = gql`
  ${ProductFragment}
  query product($id: ID!) {
    product(id: $id) {
      ...CustomProductFragment
    }
  }
`;

export const MUTATION_CREATE_VARIANT_FOR_PRODUCT = gql`
  ${ProductFragment}
  mutation CreateVariantForProduct($input: CreateVariantForProductInput!) {
    createVariantForProduct(input: $input) {
      ...CustomProductFragment
    }
  }
`;

export const MUTATION_DUPLICATE_PRODUCT = gql`
  ${ProductFragment}
  mutation DuplicateProduct($input: DuplicateProductInput!) {
    duplicateProduct(input: $input) {
      ...CustomProductFragment
    }
  }
`;

export const MUTATION_SEND_PRODUCT_INBOUND_RETAILER = gql`
  mutation SendProductInboundRetailer($productId: ID!) {
    sendProductInboundRetailer(productId: $productId) {
      success
    }
  }
`;

export const MUTATION_ADD_TO_PARENT_PRODUCT = gql`
  ${ProductFragment}
  mutation AddToParentProduct($input: AddToParentProductInput!) {
    addToParentProduct(input: $input) {
      ...CustomProductFragment
    }
  }
`;

export const MUTATION_REMOVE_PRODUCT_FROM_PARENT = gql`
  ${ProductFragment}
  mutation RemoveProductFromParent($productId: ID!) {
    removeProductFromParent(productId: $productId) {
      ...CustomProductFragment
    }
  }
`;

export const MUTATION_REMOVE_ALL_VARIANT_DATA_FROM_PRODUCT = gql`
  ${ProductFragment}
  mutation RemoveAllVariantDataFromProduct($productId: ID!) {
    removeAllVariantDataFromProduct(productId: $productId) {
      ...CustomProductFragment
    }
  }
`;

export const QUERY_PRODUCT_OPTION_GROUP_TEMPLATES = gql`
  ${PaginatedListMetaNoNestingFragment}
  ${ProductOptionGroupTemplateNoNestingFragment}
  query productOptionGroupTemplates {
    productOptionGroupTemplates {
      items {
        ...ProductOptionGroupTemplateNoNesting
      }
      meta {
        ...PaginatedListMetaNoNesting
      }
    }
  }
`;

export const QUERY_PRODUCT_OPTION_GROUP_TEMPLATE = gql`
  ${ProductOptionGroupTemplateNoNestingFragment}
  query productOptionGroupTemplate($id: ID!) {
    productOptionGroupTemplate(id: $id) {
      ...ProductOptionGroupTemplateNoNesting
      options {
        name
      }
    }
  }
`;

export const MUTATION_CONVERT_MAIN_PRODUCT_FROM_TEMPLATE = gql`
  ${ProductFragment}
  mutation ConvertMainProductFromTemplate(
    $input: ConvertMainProductFromTemplateInput!
  ) {
    convertMainProductFromTemplate(input: $input) {
      ...CustomProductFragment
    }
  }
`;

export const DANGEROUSLY_DELETE_ALL_PRODUCT_VARIANTS_AND_ASSOCIATED_DATA = gql`
  mutation DangerouslyDeleteAllProductVariantsAndAssociatedData {
    dangerouslyDeleteAllPartnerProductVariantsData
  }
`;
